"use client";

import { cn } from "@/utils/cn";
import * as AccordionPrimitive from "@radix-ui/react-accordion";
import { ChevronDown } from "lucide-react";

import * as React from "react";

import { memo } from "react";

/**
 * Accordion — shadcn primitive migrated to class-selector + co-located CSS.
 *
 * REFERENCE PATTERN: class-selector + co-located CSS (see shadcn-authoring.md
 * commit b35c0a615). Tier-4 long-tail Radix wrapper migration; no cva to
 * remove (the source `accordion.tsx` was already Tailwind-utility-driven via
 * cn()), just classname migration to BEM + token-direct CSS.
 *
 * 4 exports preserved EXACTLY:
 *   Accordion        — verbatim re-export of AccordionPrimitive.Root
 *   AccordionItem    — forwardRef wrapper, .dw-accordion__item identity
 *   AccordionTrigger — forwardRef wrapper, .dw-accordion__trigger identity
 *                      (composes AccordionPrimitive.Header + Trigger + ChevronDown)
 *   AccordionContent — forwardRef wrapper, .dw-accordion__content identity
 *                      (Radix Content + padded inner div)
 *
 * Internal BEM sub-elements:
 *   __item     — Radix Item (carries border-bottom separator)
 *   __trigger  — Radix Trigger (clickable header)
 *   __chevron  — ChevronDown lucide icon inside Trigger; rotates 180° on open
 *   __content  — Radix Content (animation host)
 *   __content-inner — padded <div> wrapping the consumer's children
 *
 * Radix data-state preservation: Radix emits `data-state="open"` /
 * `data-state="closed"` on Item, Trigger, and Content. The CSS layers atop
 * those attributes — chevron rotation is `[data-state="open"] .dw-accordion__chevron`,
 * content open/close animation is keyed off `[data-state]` on .__content.
 *
 * Animation: the Radix Accordion exposes the resolved content height via
 * the CSS variable `--radix-accordion-content-height`. The keyframes
 * `dw-accordion-content-down` / `dw-accordion-content-up` interpolate
 * height between 0 and that var, matching the pre-migration `animate-
 * accordion-down` / `animate-accordion-up` Tailwind keyframes (defined in
 * tailwind.config.ts:170-191; left in place — Tailwind utilities still
 * power consumer overrides). Timing: var(--duration-slow) (200ms),
 * matching the pre-migration `0.2s ease-out`.
 *
 * Reduced motion: `@media (prefers-reduced-motion: reduce)` zeroes the
 * content open/close animation and the chevron rotation transition.
 *
 * Strategy A specificity: base class has no axis-divergent values — there
 * is only one variant per identity. (Accordion has no variant axis like
 * Button's variant + size; the only "axis" is Radix data-state, which is
 * a behavioral attribute, not a visual variant enum.)
 *
 * Source: docs/implementation-blueprint.md §8.3 (class-selector pattern);
 *         .claude/agent-prompts/shadcn-authoring.md (binding template, commit b35c0a615);
 *         .claude/agent-prompts/atom-authoring-class-selector.md (shared rules);
 * src/components/ui/shadcn/button.tsx (shadcn precedent, commit b35c0a615);
 *         src/components/ui/shadcn/dialog.tsx (Radix-wrapped + animation precedent).
 */

const Accordion = AccordionPrimitive.Root;

const AccordionItem = memo(
    React.forwardRef<React.ComponentRef<typeof AccordionPrimitive.Item>, React.ComponentPropsWithoutRef<typeof AccordionPrimitive.Item>>(({ className, ...props }, ref) => <AccordionPrimitive.Item ref={ref} className={cn("dw-accordion__item", className)} data-component="AccordionItem" data-location="src/components/ui/shadcn/accordion.tsx" {...props} />)
);
AccordionItem.displayName = "AccordionItem";

const AccordionTrigger = memo(
    React.forwardRef<React.ComponentRef<typeof AccordionPrimitive.Trigger>, React.ComponentPropsWithoutRef<typeof AccordionPrimitive.Trigger>>(({ className, children, ...props }, ref) => (
        <AccordionPrimitive.Header className="dw-accordion__header" data-component="AccordionTrigger" data-location="src/components/ui/shadcn/accordion.tsx">
            <AccordionPrimitive.Trigger ref={ref} className={cn("dw-accordion__trigger", className)} {...props}>
                {children}
                <ChevronDown className="dw-accordion__chevron" aria-hidden />
            </AccordionPrimitive.Trigger>
        </AccordionPrimitive.Header>
    ))
);
AccordionTrigger.displayName = AccordionPrimitive.Trigger.displayName;

const AccordionContent = memo(
    React.forwardRef<React.ComponentRef<typeof AccordionPrimitive.Content>, React.ComponentPropsWithoutRef<typeof AccordionPrimitive.Content>>(({ className, children, ...props }, ref) => (
        <AccordionPrimitive.Content ref={ref} className="dw-accordion__content" data-component="AccordionContent" data-location="src/components/ui/shadcn/accordion.tsx" {...props}>
            <div className={cn("dw-accordion__content-inner", className)}>{children}</div>
        </AccordionPrimitive.Content>
    ))
);
AccordionContent.displayName = AccordionPrimitive.Content.displayName;

export { Accordion, AccordionContent, AccordionItem, AccordionTrigger };
